import React from 'react';
import {
  Settings, Sun, Moon, Cloud, Bell,
  Crown, Shield, User, MessageSquare, LogOut,
  ChevronRight, ExternalLink, Zap, Building2,
} from 'lucide-react';
import { useClerk } from '@clerk/clerk-react';

// Subscription tier icons — 3-tier GIS subscription model
const TIER_ICONS = {
  pro: { icon: Crown, color: 'text-blue-600' },
  basic: { icon: Zap, color: 'text-teal-500' },
  free: { icon: Zap, color: 'text-teal-500' }, // backward compat
};

// Subscription tier names
const TIER_NAMES = {
  pro: 'Pro',
  basic: 'Basic',
  free: 'Basic', // backward compat
};

export default function ProfileDropdown({
  user,
  theme,
  setTheme,
  userTier = 'basic',
  onNavigate,
  onClose,
  t = (key) => key,
}) {
  const { signOut } = useClerk();
  const isDark = theme === 'dark';
  
  const TierIcon = TIER_ICONS[userTier]?.icon || User;
  const tierColor = TIER_ICONS[userTier]?.color || 'text-slate-400';
  const isAdmin = user?.publicMetadata?.admin;

  const handleSignOut = () => {
    signOut();
    onClose();
  };

  return (
    <div className={`
      w-72 rounded-lg shadow-dropdown z-50
      ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
      border overflow-hidden
    `}>
      {/* User Info Header */}
      <div className={`p-4 border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary-700 text-white flex items-center justify-center text-lg font-semibold">
            {user?.firstName?.charAt(0) || user?.emailAddresses?.[0]?.emailAddress?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {user?.fullName || user?.firstName || 'User'}
            </p>
            <p className={`text-sm truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {user?.emailAddresses?.[0]?.emailAddress || ''}
            </p>
          </div>
        </div>
      </div>

      {/* Settings Section */}
      <div className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
        {/* Settings - Admin only */}
        {isAdmin && (
        <button
          onClick={() => onNavigate('admin')}
          className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
            ${isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
        >
          <Settings size={18} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
          <span className="flex-1 text-left">{t('settings') || 'Settings'}</span>
          <ChevronRight size={16} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
        </button>
        )}

        {/* Theme Toggle */}
        <div className={`flex items-center justify-between px-4 py-3 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
          <div className="flex items-center gap-3">
            {isDark ? (
              <Moon size={18} className="text-slate-400" />
            ) : (
              <Sun size={18} className="text-slate-500" />
            )}
            <span className="text-sm">{t('theme') || 'Theme'}</span>
          </div>
          <button
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            className={`
              relative w-12 h-6 rounded-full transition-colors
              ${isDark ? 'bg-primary-600' : 'bg-slate-200'}
            `}
          >
            <div className={`
              absolute top-1 w-4 h-4 rounded-full transition-transform bg-white shadow
              ${isDark ? 'translate-x-6' : 'translate-x-1'}
            `} />
          </button>
        </div>

        {/* Cloud Sync Status - Data is stored in Supabase */}
        <div className={`flex items-center justify-between px-4 py-3 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
          <div className="flex items-center gap-3">
            <Cloud size={18} className="text-primary-500" />
            <span className="text-sm">Cloud Sync</span>
          </div>
          <span className="text-sm text-primary-600">Active</span>
        </div>

        {/* My Alerts */}
        <button
          onClick={() => onNavigate('alerts')}
          className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
            ${isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
        >
          <Bell size={18} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
          <span className="flex-1 text-left">My Alerts</span>
          <ChevronRight size={16} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
        </button>
      </div>

      {/* Subscription Badge */}
      <div className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
        <button
          onClick={() => onNavigate('billing')}
          className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
            ${isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
        >
          <TierIcon size={18} className={tierColor} />
          <span className="flex-1 text-left">
            {isAdmin ? 'Unlimited' : TIER_NAMES[userTier]}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
            {isAdmin ? '∞' : 'Manage'}
          </span>
        </button>
      </div>

      {/* Help & Legal */}
      <div className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
        <a
          href="/help"
          className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
            ${isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
        >
          <MessageSquare size={18} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
          <span className="flex-1 text-left">Help Center</span>
          <ExternalLink size={14} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
        </a>
        <a
          href="/about"
          className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
            ${isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
        >
          <Building2 size={18} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
          <span className="flex-1 text-left">About Us</span>
          <ExternalLink size={14} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
        </a>
        <a
          href="/privacy"
          className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
            ${isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
        >
          <Shield size={18} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
          <span className="flex-1 text-left">Privacy Policy</span>
          <ExternalLink size={14} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
        </a>
      </div>

      {/* Sign Out */}
      <button
        onClick={handleSignOut}
        className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
          ${isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'}`}
      >
        <LogOut size={18} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
        <span>Sign Out</span>
      </button>
    </div>
  );
}
